import React, { useCallback, useContext, useEffect, useState } from 'react';
import { FaSyncAlt } from 'react-icons/fa';
import AgentContext from '../../../context/admin/Agent.tsx';
import { useUser } from '../../../context/UserContext.tsx';
import { useAgentPermissions } from '../../../helpers/useAgentPermissions.tsx';
import '../../../styles/admin/BettingSetup.css';
import AgentPlayoutSetup from './AgentPlayoutSetup.tsx';
import PlayerLimitsSetup from './PlayerLimitsSetup.tsx';
import UnsavedChangesModal from './UnsavedChangesModal.tsx';

type SetupTab = 'players' | 'agents';

function BettingSetup() {
    const { user: currentUser } = useUser();
    const { allPlayers, allAgents, loading: agentLoading } = useContext(AgentContext);

    const [activeTab, setActiveTab] = useState<SetupTab>('players');
    const [isLoading, setIsLoading] = useState(true);
    const [playersDirty, setPlayersDirty] = useState(false);
    const [agentsDirty, setAgentsDirty] = useState(false);
    const [toast, setToast] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
    const [pendingTab, setPendingTab] = useState<SetupTab | null>(null);
    const { allowAccessToAllLimits } = useAgentPermissions(currentUser);
    const canEditOwnAgentLimits = currentUser?.userType === 'subAgent' && allowAccessToAllLimits;

    if (!currentUser) {
        throw new Error('User not authenticated');
    }

    useEffect(() => {
        if (agentLoading && allPlayers.length === 0) {
            setIsLoading(true);
            return;
        }
        setIsLoading(false);
    }, [agentLoading, allPlayers.length]);

    useEffect(() => {
        const masterCanSee = currentUser.userType === 'masterAgent' && allAgents.length > 0;
        if (activeTab === 'agents' && !masterCanSee && !canEditOwnAgentLimits) {
            setActiveTab('players');
        }
    }, [activeTab, currentUser.userType, allAgents.length, canEditOwnAgentLimits]);

    const showToast = useCallback((type: 'success' | 'error', message: string) => {
        setToast({ type, message });
        setTimeout(() => setToast(null), 3000);
    }, []);

    const isMasterAgent = currentUser.userType === 'masterAgent';
    const showAgentTab = (isMasterAgent && allAgents.length > 0) || canEditOwnAgentLimits;

    const applyTabSwitch = (tab: SetupTab) => {
        if (activeTab === 'players') setPlayersDirty(false);
        else setAgentsDirty(false);
        setActiveTab(tab);
        setPendingTab(null);
    };

    const switchTab = (tab: SetupTab) => {
        if (tab === activeTab) return;

        const dirty = activeTab === 'players' ? playersDirty : agentsDirty;
        if (dirty) {
            setPendingTab(tab);
            return;
        }

        applyTabSwitch(tab);
    };

    return (
        <div className="betting-setup container-fluid mt-2">
            <div className="container-body betting-setup-header">
                <div className="betting-setup-tabs">
                    <button
                        type="button"
                        className={`betting-setup-tab ${activeTab === 'players' ? 'active' : ''}`}
                        onClick={() => switchTab('players')}
                    >
                        Player Limits
                    </button>
                    {showAgentTab && (
                        <button
                            type="button"
                            className={`betting-setup-tab ${activeTab === 'agents' ? 'active' : ''}`}
                            onClick={() => switchTab('agents')}
                        >
                            Agent Payout
                        </button>
                    )}
                </div>
            </div>

            {isLoading ? (
                <div className="betting-setup-loader">
                    <FaSyncAlt className="betting-setup-loader-icon" />
                </div>
            ) : activeTab === 'players' ? (
                <PlayerLimitsSetup onDirtyChange={setPlayersDirty} onToast={showToast} />
            ) : (
                <AgentPlayoutSetup onDirtyChange={setAgentsDirty} onToast={showToast} />
            )}

            {toast && (
                <div className={`betting-setup-toast ${toast.type}`}>
                    {toast.message}
                </div>
            )}

            <UnsavedChangesModal
                show={pendingTab !== null}
                onKeepEditing={() => setPendingTab(null)}
                onDiscard={() => {
                    if (pendingTab) applyTabSwitch(pendingTab);
                }}
            />
        </div>
    );
}

export default BettingSetup;
